/**
 * Integration Builder — the no-code route for a new integration. Six steps
 * over one draft: contract, identity, pricing, copy, connection, review. Each
 * step saves its own section; the rail shows which steps still need work.
 */
import { ArrowLeft, BookOpen, Check, Loader2, Sparkles, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { adminClient, type IntegrationDraft, type IntegrationDraftGeneration, type IntegrationDraftUpdate, type IntegrationSourceApp } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminBreadcrumbs } from '@/components/shell/AdminBreadcrumbs';
import { PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminPageLoadError } from '@/components/shell/AdminPageLoadError';
import { Button } from '@/components/ui/button';
import { handbookUrl } from '@/handbook/handbookUrls';
import { scrollAdminMainToTop } from '@/lib/scroll';
import { cn } from '@/lib/utils';

import { ActivityPanel } from './ActivityPanel';
import { AppSourcePanel } from './AppSourcePanel';
import { BUILDER_STEPS, statusMetaFor, copyFor, errorMessage, isEditable, stepComplete, takeGeneration, type BuilderStepId } from './builderModel';
import { IntegrationLogo } from './builderUi';
import { LiveDraftPanel } from './LiveDraftPanel';
import { ContractStep } from './steps/ContractStep';
import { CopyStep } from './steps/CopyStep';
import { CredentialsStep } from './steps/CredentialsStep';
import { IdentityStep } from './steps/IdentityStep';
import { PricingStep } from './steps/PricingStep';
import { ReviewStep } from './steps/ReviewStep';

/** What "Generate with AI" filled in, shown once when the author lands on the new draft. */
function GenerationBanner({ generation, onDismiss }: { generation: IntegrationDraftGeneration; onDismiss: () => void }) {
  return (
    <div className="mb-6 rounded-xl border border-brand/30 bg-brand-muted/20 px-4 py-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
          <div className="space-y-1.5">
            <div className="font-medium">Drafted with AI. Review every step before you submit it.</div>
            {generation.filled.length ? <div className="text-xs text-muted-foreground">Filled in: {generation.filled.join(', ')}.</div> : null}
            {generation.credentialsNeeded.length ? (
              <div className="text-xs text-muted-foreground">
                Still yours to do: add a logo on the Identity step and store {generation.credentialsNeeded.join(', ')} on the Connection step.
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">Still yours to do: add a logo on the Identity step (upload one or generate it).</div>
            )}
            {generation.translation.failed.length ? (
              <div className="text-xs text-muted-foreground">Not translated yet: {generation.translation.failed.join(', ')}. Use Translate on the Copy step.</div>
            ) : null}
            {generation.notes.length ? (
              <ul className="list-disc space-y-0.5 pl-4 text-xs text-muted-foreground">
                {generation.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function isStepId(value: string | null): value is BuilderStepId {
  return BUILDER_STEPS.some((step) => step.id === value);
}

export function IntegrationBuilderPage() {
  const { draftId = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('step');
  const step: BuilderStepId = isStepId(requested) ? requested : 'contract';
  const [draft, setDraft] = useState<IntegrationDraft>();
  const [app, setApp] = useState<IntegrationSourceApp>();
  const [loadError, setLoadError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [generation, setGeneration] = useState<IntegrationDraftGeneration | undefined>(() => takeGeneration(draftId));
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoadError(undefined);
    adminClient
      .getIntegrationDraft(draftId)
      .then((value) => {
        if (cancelled) return;
        setDraft(value);
        setApp(value.app);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(errorMessage(error));
      });
    return () => {
      cancelled = true;
    };
  }, [draftId, reloadKey]);

  const goTo = useCallback(
    (next: BuilderStepId) => {
      setSearchParams({ step: next });
      scrollAdminMainToTop('smooth');
    },
    [setSearchParams],
  );

  const save = useCallback(
    async (update: IntegrationDraftUpdate) => {
      setSaving(true);
      try {
        const next = await adminClient.updateIntegrationDraft(draftId, update);
        setDraft(next);
        return next;
      } catch (error) {
        toast.error(errorMessage(error));
        return undefined;
      } finally {
        setSaving(false);
      }
    },
    [draftId],
  );

  const remove = async () => {
    if (!draft || !window.confirm(`Delete the ${draft.integrationId} draft and every credential stored for it?`)) return;
    try {
      await adminClient.deleteIntegrationDraft(draft.draftId);
      toast.success('Draft deleted');
      navigate('/integrations/builder');
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  if (loadError) {
    return (
      <AdminPageLoadError
        message={loadError}
        onRetry={() => setReloadKey((key) => key + 1)}
        backTo="/integrations/builder"
        backLabel="Back to Integration Builder"
      />
    );
  }
  if (!draft) {
    return (
      <div className={cn(PAGE_CONTAINER_CLASS, 'flex items-center gap-2 text-sm text-muted-foreground')}>
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading draft…
      </div>
    );
  }

  const readOnly = !isEditable(draft.status);
  const title = copyFor(draft, 'en').title || draft.integrationId;
  const stepIndex = BUILDER_STEPS.findIndex((item) => item.id === step);
  const next = () => goTo(BUILDER_STEPS[Math.min(stepIndex + 1, BUILDER_STEPS.length - 1)].id);
  const stepProps = { draft, readOnly, saving, save, replace: setDraft, next };
  // A Studio app's endpoints, pricing and connection belong to the app.
  const appStepProps = { ...stepProps, readOnly: readOnly || Boolean(draft.appSource) };

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminBreadcrumbs items={[{ label: 'Integration Builder', to: '/integrations/builder' }, { label: title }]} />
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <IntegrationLogo src={draft.logoDataUrl} color={draft.brandColor} title={title} size="lg" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-2xl font-bold">{title}</h1>
              <StatusPill tone={statusMetaFor(draft).tone}>{statusMetaFor(draft).label}</StatusPill>
            </div>
            <div className="mt-1 font-mono text-xs text-muted-foreground">
              {draft.integrationId}
              {draft.capability ? ` · ${draft.capability}` : ''}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <a href={handbookUrl('/handbook/integrations/no-code')} target="_blank" rel="noreferrer">
              <BookOpen className="mr-2 h-3.5 w-3.5" />
              Guide
            </a>
          </Button>
          {draft.status === 'approved' || draft.status === 'published' || draft.hasRelease || (draft.appSource && !app?.deleted) ? null : (
            <Button variant="ghost" size="sm" onClick={() => void remove()} aria-label="Delete draft">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {generation ? <GenerationBanner generation={generation} onDismiss={() => setGeneration(undefined)} /> : null}

      {draft.appSource ? <AppSourcePanel draft={draft} app={app} editable={!readOnly} replace={setDraft} /> : null}

      <LiveDraftPanel draft={draft} replace={setDraft} />

      {readOnly ? (
        <div className="mb-6 rounded-xl border border-brand/30 bg-brand-muted/20 px-4 py-3 text-sm">
          {draft.hasDraftChanges
            ? draft.status === 'in_review'
              ? 'The draft changes are in review, so they are read-only. An integration approver approves or rejects them above.'
              : 'The draft changes are approved, so they are read-only. An integration approver applies them above, or take them back to editing.'
            : draft.status === 'in_review'
              ? 'This draft is in review, so it is read-only. A reviewer can approve it or request changes on the Review step.'
              : draft.status === 'published'
                ? draft.hasRelease
                  ? 'You are looking at the live version, so it is read-only. Select Edit above to change it: you get a working copy, and customers keep the live version until your changes are approved and applied.'
                  : 'This integration is live for the RapidSOS team, so it is read-only. Select Edit on the Review step to change it.'
                : 'This integration is approved, so it is read-only. Publish it or reopen it for changes on the Review step.'}
        </div>
      ) : draft.hasDraftChanges ? (
        <div className="mb-6 rounded-xl border border-border bg-card/40 px-4 py-3 text-sm text-muted-foreground">
          You are editing the draft changes. Every step is editable, including pricing and the connection. Customers keep the live version until the
          changes are approved and applied.
        </div>
      ) : null}

      <div className="flex flex-col gap-8 lg:flex-row">
        <nav aria-label="Builder steps" className="lg:w-60 lg:shrink-0">
          <div className="lg:sticky lg:top-6">
            <ol className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:gap-1 lg:overflow-visible lg:pb-0">
              {BUILDER_STEPS.map((item, index) => {
                const active = item.id === step;
                const done = stepComplete(draft, item.id);
                return (
                  <li key={item.id} className="shrink-0">
                    <button
                      type="button"
                      onClick={() => goTo(item.id)}
                      aria-current={active ? 'step' : undefined}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                        active ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold',
                          done ? 'border-success/40 bg-success-bg text-success' : active ? 'border-foreground/40' : 'border-border',
                        )}
                      >
                        {done ? <Check className="h-3 w-3" /> : index + 1}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">{item.label}</span>
                        <span className="hidden truncate text-[11px] text-muted-foreground lg:block">{item.hint}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <Link
              to="/integrations/builder"
              className="mt-6 hidden items-center gap-2 px-3 text-xs text-muted-foreground hover:text-foreground lg:flex"
            >
              <ArrowLeft className="h-3 w-3" />
              All drafts
            </Link>
          </div>
        </nav>

        <div className="min-w-0 flex-1" key={`${step}-${draft.draftId}`}>
          {step === 'contract' ? <ContractStep {...appStepProps} /> : null}
          {step === 'identity' ? <IdentityStep {...stepProps} /> : null}
          {step === 'copy' ? <CopyStep {...stepProps} /> : null}
          {step === 'pricing' ? <PricingStep {...appStepProps} /> : null}
          {step === 'credentials' ? <CredentialsStep {...appStepProps} /> : null}
          {step === 'review' ? <ReviewStep {...stepProps} goTo={goTo} /> : null}
          <ActivityPanel draftId={draft.draftId} updatedAt={draft.updatedAt} />
        </div>
      </div>
    </div>
  );
}
